import { Check } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Logo } from '@/components/brand/Logo'
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion'
import { cn } from '../../lib/utils'

// The "AI analysis" sequence from the product spec (§2.1) — shown after the
// visitor confirms their carrier, while indications are generated.
const STEPS = [
  'Analyzing your safety history...',
  'Checking ISS score...',
  'Benchmarking fleet...',
  'Estimating prices...',
]

// ~5s total (4 × 1.25s) — matches the "about 5 seconds" copy, long enough to
// land the analysis moment, short enough not to feel stuck.
const STEP_DURATION_MS = 1250
const STEP_TRANSITION_MS = 350
// How long the whole-screen exit animation plays before we tell the parent
// we're done — it swaps us out for the results screen right after.
const SCREEN_EXIT_MS = 200

export function AiAnalysisLoading({ onDone }: { onDone?: () => void }) {
  // Under reduced motion we freeze to a single static frame (see render below).
  // The step/exit timers and `onDone` hand-off timing are left untouched, so the
  // funnel behaves exactly the same — only the visuals are frozen, which also
  // keeps the design-system capture of this screen deterministic.
  const reduceMotion = usePrefersReducedMotion()
  const [completed, setCompleted] = useState(0)
  const [isExiting, setIsExiting] = useState(false)
  const currentIndex = Math.min(completed, STEPS.length - 1)

  // Keep the previous step mounted just long enough to play its exit
  // animation while the new one slides in on top of it.
  const previousIndexRef = useRef(currentIndex)
  const [outgoingIndex, setOutgoingIndex] = useState<number | null>(null)

  useEffect(() => {
    const previousIndex = previousIndexRef.current
    if (previousIndex === currentIndex) return
    previousIndexRef.current = currentIndex
    setOutgoingIndex(previousIndex)
    const timer = setTimeout(() => setOutgoingIndex(null), STEP_TRANSITION_MS)
    return () => clearTimeout(timer)
  }, [currentIndex])

  // The parent passes a fresh `onDone` closure on every render (it's polling
  // for indications), so we stash it in a ref rather than depend on it
  // directly — otherwise a parent re-render mid-exit would restart the timer
  // and the screen would never hand off.
  const onDoneRef = useRef(onDone)
  useEffect(() => {
    onDoneRef.current = onDone
  }, [onDone])

  useEffect(() => {
    if (completed >= STEPS.length) {
      setIsExiting(true)
      const timer = setTimeout(() => onDoneRef.current?.(), SCREEN_EXIT_MS)
      return () => clearTimeout(timer)
    }
    const timer = setTimeout(() => setCompleted((c) => c + 1), STEP_DURATION_MS)
    return () => clearTimeout(timer)
  }, [completed])

  // Reduced motion: pin to the final step with no entrance/exit/spin animation.
  const shownIndex = reduceMotion ? STEPS.length - 1 : currentIndex

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-10 py-16',
        !reduceMotion && (isExiting ? 'animate-screen-out' : 'animate-screen-in')
      )}
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <Logo symbolOnly width={56} aria-label="" className={cn(!reduceMotion && 'animate-spin-ease')} />
        <h3 className="text-foreground text-lg font-semibold">Loading estimates</h3>
      </div>
      <div className="relative h-6 w-full max-w-md overflow-hidden">
        {!reduceMotion && outgoingIndex !== null && <StepRow label={STEPS[outgoingIndex]} exiting />}
        <StepRow key={shownIndex} label={STEPS[shownIndex]} animate={!reduceMotion} />
      </div>
    </div>
  )
}

function StepRow({
  label,
  exiting,
  animate = true,
}: {
  label: string
  exiting?: boolean
  animate?: boolean
}) {
  return (
    <div
      className={cn(
        'absolute inset-x-0 flex items-center justify-center gap-3',
        animate && (exiting ? 'animate-step-out' : 'animate-step-in')
      )}
    >
      <Check className="size-4 shrink-0 text-neutral-300" strokeWidth={3} />
      <span className="text-md text-ink-300 font-medium">{label}</span>
    </div>
  )
}
